import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Form, FormField, FormItem, FormControl, FormMessage } from 'components/ui/form';
import { useTranslation } from 'react-i18next';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { Value } from 'react-phone-number-input';
import { Textarea } from 'components/ui/textarea';
import { CreateOrganizationRequest } from '../../services/organization-service';
import { useCreateOrganization } from '../../hooks/use-organization';
import { validatePhoneNumber } from 'utils/validation/phone-validation';

// Function to get only the country calling code from a phone number
/* const getParsePhoneNumber = (phoneNumber: string): string | undefined => {
    if (!phoneNumber) return "";

    try {
        const parsed = parsePhoneNumber(phoneNumber);
        if (parsed && parsed.countryCallingCode) {
            return `${parsed.nationalNumber}`;
        }
    } catch (error) {
        console.error('Error parsing phone number:', error);
    }

    return undefined;
}; */

const createOrganizationSchema = (t: (key: string) => string) => z.object({
    name: z.string().min(1, t('NAME_IS_REQUIRED')).max(30, t('NAME_IS_TOO_LONG')),
    email: z.string()
        .min(1, t('EMAIL_IS_REQUIRED'))
        .email(t('INVALID_EMAIL_ADDRESS'))
        .refine(
            (val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val),
            { message: t('INVALID_EMAIL_ADDRESS') }
        ),
    phoneNumber: z.string()
        .refine(
            (val) => !val || /^\+[1-9]\d{1,14}$/.test(val),
            { message: t('INVALID_PHONE_NUMBER') }
        )
        .refine(
            validatePhoneNumber,
            { message: t('PHONE_NUMBER_TOO_SHORT') }
        )
        .optional(),
    websiteUrl: z.string()
        .refine(
            (val) => !val || /^https?:\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(val),
            { message: t('INVALID_WEBSITE_URL') }
        )
        .optional()
        .or(z.literal('')),
    address: z.string().min(1, t('ADDRESS_IS_REQUIRED')).max(150, t('MAX_150_CHARACTERS')),
});

interface AddOrganizationProps {
    onClose: () => void;
}

export const AddOrganization: React.FC<AddOrganizationProps> = ({ onClose }) => {
    const { t } = useTranslation();

    const form = useForm<CreateOrganizationRequest>({
        resolver: zodResolver(createOrganizationSchema(t)),
        defaultValues: {
            name: '',
            email: '',
            phoneNumber: '',
            websiteUrl: '',
            address: '',
        },
    });

    const { control, handleSubmit, setValue } = form;

    const { mutate: createOrganization, isPending } = useCreateOrganization({
        onSuccess: () => {
            onClose();
        },
    });

    const onSubmit = async (data: CreateOrganizationRequest) => {
        // Check if phone number is only a country code (like +880, +1, +44, etc.)
        const isOnlyCountryCode = data.phoneNumber && data.phoneNumber.match(/^\+\d{1,4}$/) && data.phoneNumber.length <= 4;
        const payload = {
            ...data,
            code: "", // This is not country code, it is organization code
            phoneNumber: isOnlyCountryCode ? "" : data.phoneNumber || ""
        };
        createOrganization(payload);
    };

    const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <DialogContent
            className="rounded-md sm:max-w-[700px] overflow-y-auto max-h-screen"
            onClick={handleDialogClick}
        >
            <DialogHeader>
                <DialogTitle>{t('ADD_ORGANIZATION')}</DialogTitle>
                <DialogDescription>{t('KEEP_DETAILS_ACCURATE_UP_TO_DATE')}</DialogDescription>
            </DialogHeader>
            <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                            control={control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="name">{t('ORGANIZATION_NAME')} *</Label>
                                    <FormControl>
                                        <Input id="name" placeholder={t('ENTER_ORGANIZATION_NAME')} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="email">{t('EMAIL')} *</Label>
                                    <FormControl>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder={t('ENTER_EMAIL')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="phoneNumber"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="phoneNumber">{t('PHONE_NUMBER')}</Label>
                                    <FormControl>
                                        <UIPhoneInput
                                            value={field.value as Value}
                                            onChange={(value: Value) => setValue('phoneNumber', value as string)}
                                            placeholder={t('ENTER_PHONE_NUMBER')}
                                            defaultCountry="CH"
                                            countryCallingCodeEditable={false}
                                            international
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="websiteUrl"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="websiteUrl">{t('WEBSITE')}</Label>
                                    <FormControl>
                                        <Input
                                            id="websiteUrl"
                                            placeholder={t('ENTER_WEBSITE_URL')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <FormField
                        control={control}
                        name="address"
                        render={({ field }) => (
                            <FormItem>
                                <Label htmlFor="address">{t('ADDRESS')} *</Label>
                                <FormControl>
                                    <Textarea
                                        id="address"
                                        placeholder={t('ENTER_ADDRESS')}
                                        className="resize-none"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="w-full sm:w-auto"
                            disabled={isPending}
                        >
                            {t('CANCEL')}
                        </Button>
                        <Button
                            type="submit"
                            className="w-full sm:w-auto"
                            disabled={isPending}
                        >
                            {isPending ? t('SAVING') : t('SAVE')}
                        </Button>
                    </DialogFooter>
                </form>
            </Form>
        </DialogContent>
    );
}; 